import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const genSession = () => 'concierge_' + Math.random().toString(36).slice(2, 10) + '_' + Date.now();

/**
 * Floating AI concierge chat widget.
 */
export default function ConciergeChat() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: 'assistant', text: t('carvana.concierge.greet', 'Bonjour! Je suis votre concierge IA. Comment puis-je vous aider avec l\'achat de votre véhicule?') },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(genSession());
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMsgs((m) => [...m, { role: 'user', text }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/concierge/chat`, {
        session_id: sessionId.current,
        message: text,
        lang: i18n.language?.startsWith('en') ? 'en' : 'fr',
      });
      setMsgs((m) => [...m, { role: 'assistant', text: res.data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: 'assistant', text: t('carvana.concierge.error', 'Désolé, une erreur est survenue. Réessayez.') }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 left-4 sm:bottom-6 sm:left-6 z-50 bg-gradient-to-br from-[#0c2340] to-[#3b82f6] text-white p-4 rounded-full shadow-2xl hover:scale-105 transition-transform flex items-center gap-2"
          data-testid="concierge-open-btn"
        >
          <Sparkles className="h-5 w-5" />
          <span className="hidden sm:inline text-sm font-bold">{t('carvana.concierge.buttonLabel', 'Concierge IA')}</span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-4 left-4 z-50 w-[calc(100vw-2rem)] sm:w-[380px] h-[540px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200" data-testid="concierge-panel">
          <div className="bg-gradient-to-r from-[#0c2340] to-[#3b82f6] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              <div>
                <p className="font-bold text-sm">{t('carvana.concierge.title', 'Concierge IA')}</p>
                <p className="text-[10px] opacity-80">{t('carvana.concierge.online', 'En ligne 24/7 · FR/EN')}</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="concierge-close-btn"><X className="h-5 w-5" /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="concierge-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${m.role === 'user' ? 'bg-[#3b82f6] text-white rounded-br-md' : 'bg-white text-slate-800 rounded-bl-md border border-slate-200'}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl p-3"><Loader2 className="h-4 w-4 animate-spin text-[#3b82f6]" /></div></div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-slate-200 bg-white flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder={t('carvana.concierge.placeholder', 'Posez votre question...')}
              className="flex-1 border border-slate-300 rounded-full px-4 py-2 text-sm focus:border-[#3b82f6] focus:outline-none"
              data-testid="concierge-input"
            />
            <button onClick={send} disabled={loading || !input.trim()} className="bg-[#0c2340] text-white p-2 rounded-full disabled:opacity-50" data-testid="concierge-send-btn">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
